import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUri } from '../api';
import {
  createOrganizationAuthStorage,
  OrganizationAuth,
  OrganizationAuthStorage,
} from '../api/auth-org';
import { clearUserDataCache } from './useUserData';

// Simple auth interface for v2 - we'll use localStorage for now
interface FlikHttpAuthOptions {
  headerName: string;
  token: {
    accessTokenKey: string;
    refreshTokenKey: string;
  };
  skiplist?: {
    paths?: string[];
  };
  storage?: {
    get: (key: string) => string | null;
    set: (key: string, value: string) => void;
    remove: (key: string) => void;
  };
  // Organization-aware auth storage
  organizationStorage?: OrganizationAuthStorage;
}

// Simple auth implementation for v2
const FlikHttpAuth = (options: FlikHttpAuthOptions) => {
  // Use organization storage if available, otherwise fall back to legacy storage
  const orgStorage =
    options.organizationStorage || createOrganizationAuthStorage();

  const getAccessToken = (): string | null => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const data = orgStorage.get();
      if (data?.currentOrganization) {
        const currentOrgAuth = data.organizations.find(
          (org) => org.organization_id === data.currentOrganization,
        );
        return currentOrgAuth?.accessToken || null;
      }
      return null;
    }

    // Fall back to legacy storage
    if (options.storage) {
      return options.storage.get(options.token.accessTokenKey);
    }
    return localStorage.getItem(options.token.accessTokenKey);
  };

  const getRefreshToken = (): string | null => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const data = orgStorage.get();
      if (data?.currentOrganization) {
        const currentOrgAuth = data.organizations.find(
          (org) => org.organization_id === data.currentOrganization,
        );
        return currentOrgAuth?.refreshToken || null;
      }
      return null;
    }

    // Fall back to legacy storage
    if (options.storage) {
      return options.storage.get(options.token.refreshTokenKey);
    }
    return localStorage.getItem(options.token.refreshTokenKey);
  };

  const setAccessToken = (token: string) => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const data = orgStorage.get();
      if (data?.currentOrganization) {
        orgStorage.updateOrganization(data.currentOrganization, {
          accessToken: token,
        });
        return;
      }
    }

    // Fall back to legacy storage
    if (options.storage) {
      options.storage.set(options.token.accessTokenKey, token);
    } else {
      localStorage.setItem(options.token.accessTokenKey, token);
    }
  };

  const setRefreshToken = (token: string) => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const data = orgStorage.get();
      if (data?.currentOrganization) {
        orgStorage.updateOrganization(data.currentOrganization, {
          refreshToken: token,
        });
        return;
      }
    }

    // Fall back to legacy storage
    if (options.storage) {
      options.storage.set(options.token.refreshTokenKey, token);
    } else {
      localStorage.setItem(options.token.refreshTokenKey, token);
    }
  };

  // New methods for organization-aware auth
  const setOrganizationAuth = (
    organizationId: string,
    auth: OrganizationAuth,
  ) => {
    if (options.organizationStorage) {
      orgStorage.updateOrganization(organizationId, auth);
    }
  };

  const setCurrentOrganization = (organizationId: string | null) => {
    if (options.organizationStorage) {
      orgStorage.setCurrentOrganization(organizationId);
    }
  };

  const getCurrentOrganization = (): string | null => {
    if (options.organizationStorage) {
      const data = orgStorage.get();
      return data?.currentOrganization || null;
    }
    return null;
  };

  const getAllOrganizations = () => {
    if (options.organizationStorage) {
      return orgStorage.get()?.organizations || [];
    }
    return [];
  };

  const authenticate = async (
    email: string,
    verificationCode?: string,
    resendVerificationCode = false,
  ) => {
    const uri = getUri();

    try {
      const response = await fetch(`${uri}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant: 'public',
          emailAddress: email,
          verificationCode,
          resendVerificationCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();

      if (data.accessToken && data.refreshToken) {
        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);
      }

      return data;
    } catch (error) {
      console.error('Authentication error:', error);
      throw error;
    }
  };

  const clearSession = async () => {
    const uri = getUri();
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      // If no refresh token, just clear local storage
      if (options.organizationStorage) {
        const data = orgStorage.get();
        if (data?.currentOrganization) {
          orgStorage.removeOrganization(data.currentOrganization);
        }
      } else if (options.storage) {
        options.storage.remove(options.token.accessTokenKey);
        options.storage.remove(options.token.refreshTokenKey);
      } else {
        localStorage.removeItem(options.token.accessTokenKey);
        localStorage.removeItem(options.token.refreshTokenKey);
      }
      return;
    }

    try {
      const response = await fetch(`${uri}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      // Clear tokens regardless of response
      if (options.organizationStorage) {
        const data = orgStorage.get();
        if (data?.currentOrganization) {
          orgStorage.removeOrganization(data.currentOrganization);
        }
      } else if (options.storage) {
        options.storage.remove(options.token.accessTokenKey);
        options.storage.remove(options.token.refreshTokenKey);
      } else {
        localStorage.removeItem(options.token.accessTokenKey);
        localStorage.removeItem(options.token.refreshTokenKey);
      }

      if (!response.ok) {
        console.warn('Logout request failed, but tokens cleared locally');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear tokens even if request fails
      if (options.organizationStorage) {
        const data = orgStorage.get();
        if (data?.currentOrganization) {
          orgStorage.removeOrganization(data.currentOrganization);
        }
      } else if (options.storage) {
        options.storage.remove(options.token.accessTokenKey);
        options.storage.remove(options.token.refreshTokenKey);
      } else {
        localStorage.removeItem(options.token.accessTokenKey);
        localStorage.removeItem(options.token.refreshTokenKey);
      }
    }
  };

  const refreshAccessToken = async (): Promise<string> => {
    const uri = getUri();
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${uri}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // If refresh fails, clear session
        if (response.status === 401) {
          await clearSession();
        }

        throw new Error(
          errorData.detail || `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();

      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return data.accessToken;
      }

      throw new Error('Token refresh failed - no access token received');
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  };

  // Background refresh that doesn't clear session on failure
  const refreshAccessTokenBackground = async (): Promise<string | null> => {
    const uri = getUri();
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${uri}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Don't clear session on background refresh failure
        return null;
      }

      const data = await response.json();

      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return data.accessToken;
      }

      return null;
    } catch (error) {
      console.warn('Background token refresh failed:', error);
      return null;
    }
  };

  return {
    getAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    authenticate,
    clearSession,
    refreshAccessToken,
    refreshAccessTokenBackground,
    headerName: options.headerName,
    // Organization-aware methods
    setOrganizationAuth,
    setCurrentOrganization,
    getCurrentOrganization,
    getAllOrganizations,
  };
};

interface AuthState {
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
}

interface SignInState {
  email: string;
  verificationCode: string;
  step: 'email' | 'verification' | 'complete';
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

// Create storage functions - use localStorage for renderer process
const createStorage = () => {
  return {
    get: (key: string): string | null => {
      return localStorage.getItem(key);
    },
    set: (key: string, value: string): void => {
      localStorage.setItem(key, value);
    },
    remove: (key: string): void => {
      localStorage.removeItem(key);
    },
  };
};

// Create a shared storage instance
const sharedStorage = createStorage();

export const useIdentity = (authOptions?: FlikHttpAuthOptions) => {
  // Create auth instance with provided options or default options
  const auth = useMemo(() => {
    const defaultAuthOptions: FlikHttpAuthOptions = {
      headerName: 'x-authorization',
      token: {
        accessTokenKey: 'auth.accessToken',
        refreshTokenKey: 'auth.refreshToken',
      },
      skiplist: {
        paths: ['/auth/refresh'],
      },
      storage: sharedStorage,
      organizationStorage: createOrganizationAuthStorage(),
    };

    const finalAuthOptions = authOptions || defaultAuthOptions;
    return FlikHttpAuth(finalAuthOptions);
  }, [authOptions]);

  const [authState, setAuthState] = useState<AuthState>({
    isLoggedIn: false,
    isLoading: true,
    error: null,
  });

  const [signInState, setSignInState] = useState<SignInState>({
    email: '',
    verificationCode: '',
    step: 'email',
    isLoading: false,
    error: null,
    successMessage: null,
  });

  // Organization state
  const [currentOrganization, setCurrentOrganizationState] = useState<
    string | null
  >(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<
    OrganizationAuth[]
  >([]);

  // Flag to prevent auth check during logout
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    if (isLoggingOut) {
      return;
    }

    const checkAuthStatus = async () => {
      try {
        setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

        // Load organization data
        const orgs = auth.getAllOrganizations();
        const currentOrg = auth.getCurrentOrganization();

        setAvailableOrganizations(orgs);
        setCurrentOrganizationState(currentOrg);

        // Check if we have a refresh token
        const refreshToken = auth.getRefreshToken();

        if (!refreshToken) {
          setAuthState({ isLoggedIn: false, isLoading: false, error: null });
          return;
        }

        // If we have a refresh token, assume the user is logged in
        setAuthState({ isLoggedIn: true, isLoading: false, error: null });

        // Optionally try to refresh the access token in the background
        // Don't clear session on background refresh failure
        try {
          await auth.refreshAccessTokenBackground();
        } catch (error) {
          // Background refresh failed, but user remains logged in
          // Don't clear session - just log the error
          console.warn('Background token refresh failed:', error);
        }
      } catch (error) {
        setAuthState({
          isLoggedIn: false,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Authentication check failed',
        });
      }
    };

    checkAuthStatus();
  }, [auth, isLoggingOut]);

  const signIn = useCallback(
    async (email: string) => {
      try {
        setSignInState((prev) => ({
          ...prev,
          email,
          isLoading: true,
          error: null,
        }));

        await auth.authenticate(email);

        setSignInState((prev) => ({
          ...prev,
          step: 'verification',
          isLoading: false,
        }));
      } catch (error) {
        setSignInState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to send verification code',
        }));
      }
    },
    [auth],
  );

  const verifyCode = useCallback(
    async (verificationCode: string) => {
      try {
        setSignInState((prev) => ({
          ...prev,
          verificationCode,
          isLoading: true,
          error: null,
        }));

        await auth.authenticate(signInState.email, verificationCode);

        // Update both states together to ensure proper batching
        setAuthState({ isLoggedIn: true, isLoading: false, error: null });
        setSignInState({
          email: '',
          verificationCode: '',
          step: 'complete',
          isLoading: false,
          error: null,
          successMessage: null,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Invalid verification code';
        setSignInState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
      }
    },
    [signInState.email, auth],
  );

  const resendCode = useCallback(async () => {
    try {
      setSignInState((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
        verificationCode: '',
      }));

      await auth.authenticate(signInState.email, undefined, true);

      setSignInState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
        verificationCode: '',
        step: 'verification',
        successMessage: 'Verification code sent! Please check your email.',
      }));
    } catch (error) {
      setSignInState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to resend verification code',
        verificationCode: '',
      }));
    }
  }, [signInState.email, auth]);

  const signOut = useCallback(async () => {
    try {
      setIsLoggingOut(true);

      // Clear tokens from localStorage
      localStorage.removeItem('auth.refreshToken');
      localStorage.removeItem('auth.accessToken');

      // Clear cached user data
      clearUserDataCache();

      // Try to clear session
      try {
        await auth.clearSession();
      } catch (clearError) {
        // Session clear failed, but continuing with logout
      }

      // Add a small delay to ensure the session is fully cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      setAuthState({ isLoggedIn: false, isLoading: false, error: null });
      setSignInState({
        email: '',
        verificationCode: '',
        step: 'email',
        isLoading: false,
        error: null,
        successMessage: null,
      });

      // Re-enable auth check after a delay
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 1000);
    } catch (error) {
      // Even if there's an error, still reset the state
      setAuthState({ isLoggedIn: false, isLoading: false, error: null });
      setSignInState({
        email: '',
        verificationCode: '',
        step: 'email',
        isLoading: false,
        error: null,
        successMessage: null,
      });
      setIsLoggingOut(false);
    }
  }, [auth, signInState]);

  const resetSignIn = useCallback(() => {
    setSignInState({
      email: '',
      verificationCode: '',
      step: 'email',
      isLoading: false,
      error: null,
      successMessage: null,
    });
  }, []);

  // Organization management methods
  const switchOrganization = useCallback(
    (organizationId: string) => {
      auth.setCurrentOrganization(organizationId);
      setCurrentOrganizationState(organizationId);

      // Update available organizations to reflect the change
      const orgs = auth.getAllOrganizations();
      setAvailableOrganizations(orgs);
    },
    [auth],
  );

  const addOrganizationAuth = useCallback(
    (organizationId: string, authData: OrganizationAuth) => {
      auth.setOrganizationAuth(organizationId, authData);

      // Update available organizations
      const orgs = auth.getAllOrganizations();
      setAvailableOrganizations(orgs);

      // If this is the first organization, set it as current
      if (orgs.length === 1) {
        auth.setCurrentOrganization(organizationId);
        setCurrentOrganizationState(organizationId);
      }
    },
    [auth],
  );

  const removeOrganization = useCallback(
    (organizationId: string) => {
      // Get current organizations to check if we're removing the current one
      const orgs = auth.getAllOrganizations();
      const isCurrentOrg = currentOrganization === organizationId;

      // Remove the organization
      auth.setCurrentOrganization(null); // Clear current first
      const orgStorage = auth.getAllOrganizations();
      const updatedOrgs = orgStorage.filter(
        (org) => org.organization_id !== organizationId,
      );

      // Update the storage directly
      const orgStorageInstance = createOrganizationAuthStorage();
      orgStorageInstance.set({
        currentOrganization: null,
        organizations: updatedOrgs,
      });

      // Update state
      setAvailableOrganizations(updatedOrgs);

      if (isCurrentOrg) {
        setCurrentOrganizationState(null);
        // If there are other organizations, switch to the first one
        if (updatedOrgs.length > 0) {
          switchOrganization(updatedOrgs[0].organization_id);
        } else {
          // No organizations left, sign out
          signOut();
        }
      }
    },
    [auth, currentOrganization, switchOrganization, signOut],
  );

  const result = {
    // Auth state
    isLoggedIn: authState.isLoggedIn,
    isLoading: authState.isLoading,
    error: authState.error,

    // Sign in state
    signInState,
    setSignInState,

    // Organization state
    currentOrganization,
    availableOrganizations,

    // Actions
    signIn,
    verifyCode,
    resendCode,
    signOut,
    resetSignIn,

    // Organization management
    switchOrganization,
    addOrganizationAuth,
    removeOrganization,

    // Auth instance for direct access if needed
    auth,
  };

  return result;
};
